import React, { useEffect, useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

import { useAuth } from "../../../../firebase/AuthProvider";
import { ownerTrendSubjet$, selectedRegionIdFromHeader$, selectWaiverTypeIdFromHeader$, UpdateOnwerTrendSubject } from "../../../../services/Subjects/userSubject";
import { GetBillingAnalyticsAsync, GetParticipantAnalyticsAsync, GetTaskAnalyticsAsync } from "../../../../services/OwnerMetricsService/OwnerMetricsService";
import { OwnerBillingComplianceAnalyticsMonthDto, OwnerBillingComplianceAnalyticsResponseDto, OwnerComplianceAnalyticsMonthDto, OwnerParticipantComplianceAnalyticsResponseDto, ParticipantTrentsDto } from "../../../../interfaces";
import { OwnerGraphTrend } from "../../../../Data/SD";

const ParticipantTrends: React.FC = () => {
    const { currentUser } = useAuth();
    const [currentGraphTrend, setCurrentGraphTrend] = useState<OwnerGraphTrend>();

    const [selectedRegionId, setSelectedRegionId] = useState<number | null>();
    const [selectedWaiverId, setSelectedWaiverId] = useState<number | null>();

    // Data
    const [participantAnalytics, setParticipantAnalytics] = useState<OwnerParticipantComplianceAnalyticsResponseDto>();
    const [billingAnalytics, setBillingAnalytics] = useState<OwnerBillingComplianceAnalyticsResponseDto>();
    const [taskAnalytics, setTaskAnalytics] = useState<OwnerComplianceAnalyticsMonthDto[]>();

    useEffect(() => {
        const treandSub = ownerTrendSubjet$.subscribe({
            next: (trend) => {
                setCurrentGraphTrend(trend);
            }
        })

        const regionSub = selectedRegionIdFromHeader$.subscribe({
            next: (regionId) => {
                setSelectedRegionId(regionId);
            }
        });

        const waiverSub = selectWaiverTypeIdFromHeader$.subscribe({
            next: (waiverId) => {
                setSelectedWaiverId(waiverId);
            }
        });

        return () => {
            // Reset to default when unmounting if needed, or remove if causing side effects
            UpdateOnwerTrendSubject(OwnerGraphTrend.Participants);
            treandSub.unsubscribe();
            regionSub.unsubscribe();
            waiverSub.unsubscribe();
        }
    }, []);

    useEffect(() => {
        if (currentGraphTrend) {
            if (currentGraphTrend === OwnerGraphTrend.Participants) {
                GetParticipantAnalytics();
            } else if (currentGraphTrend === OwnerGraphTrend.Task) {
                GetTasktAnalytics();
            } else if (currentGraphTrend === OwnerGraphTrend.Billing) {
                GetBillingAnalytics();
            }
        }
    }, [currentGraphTrend, selectedRegionId, selectedWaiverId]);

    const GetParticipantAnalytics = async () => {
        if (!currentUser) return;
        const token = await currentUser.getIdToken();

        try {
            let result = await GetParticipantAnalyticsAsync(token, selectedRegionId, selectedWaiverId);
            if (result.success) {
                // We keep the raw data here as requested in previous logic, 
                // but the formatting will happen in createGraphData
                setParticipantAnalytics(result.data);
            } else {
                toast.error(result.message);
            }
        } catch (error: any) {
            const msg = error instanceof AxiosError ? error.response?.data.message : error.message || "Error to load the Participant Analytics"
            toast.error(msg);
        }
    }

    const GetTasktAnalytics = async () => {
        if (!currentUser) return;
        const token = await currentUser.getIdToken();

        try {
            let result = await GetTaskAnalyticsAsync(token, selectedRegionId, selectedWaiverId);
            if (result.success) {
                const data = result.data.map(t => ({
                    ...t,
                    month: new Date(2000, Number(t.month) - 1, 1)
                        .toLocaleString('en-US', { month: 'short' })
                }));
                result.data = data;
                setTaskAnalytics(result.data);
            } else {
                toast.error(result.message);
            }
        } catch (error: any) {
            const msg = error instanceof AxiosError ? error.response?.data.message : error.message || "Error to load the Participant Analytics"
            toast.error(msg);
        }
    }

    const GetBillingAnalytics = async () => {
        if (!currentUser) return;
        const token = await currentUser.getIdToken();

        try {
            let result = await GetBillingAnalyticsAsync(token, selectedRegionId, selectedWaiverId);
            if (result.success) {
                const data = result.data.data.map(t => ({
                    ...t,
                    month: new Date(2000, Number(t.month) - 1, 1)
                        .toLocaleString('en-US', { month: 'short' })
                }));
                result.data.data = data;
                setBillingAnalytics(result.data);
            } else {
                toast.error(result.message);
            }
        } catch (error: any) {
            const msg = error instanceof AxiosError ? error.response?.data.message : error.message || "Error to load the Participant Analytics"
            toast.error(msg);
        }
    }

    const renderLines = () => {
        switch (currentGraphTrend) {
            case OwnerGraphTrend.Participants:
                return (
                    <>
                        <Line
                            type="monotone"
                            dataKey="currentYearParticipantAdded"
                            name="Current Year"
                            stroke="#7c3aed"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="lastYearParticipantAdded"
                            name="Last Year"
                            stroke="red"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                        />
                    </>
                );

            case OwnerGraphTrend.Billing:
                return (
                    <>
                        <Line
                            type="monotone"
                            dataKey="billableAmount"
                            name="Current Year"
                            stroke="#7c3aed"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="lastYearSameMonthBillableAmount"
                            name="Last Year"
                            stroke="red"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                        />
                    </>
                );

            case OwnerGraphTrend.Task:
                return (
                    <>
                        <Line
                            type="monotone"
                            dataKey="compliancePercent"
                            name="Current Year"
                            stroke="#7c3aed"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="lastYearSameCompliancePercent"
                            name="Last Year"
                            stroke="red"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                        />
                    </>
                );

            default:
                return null;
        }
    };

    const createGraphData = (): ParticipantTrentsDto[] | OwnerBillingComplianceAnalyticsMonthDto[] | OwnerComplianceAnalyticsMonthDto[] => {
        switch (currentGraphTrend) {
            case OwnerGraphTrend.Participants:
                const participantTrends: ParticipantTrentsDto[] =
                    participantAnalytics?.currentYearLast6Months?.map(current => {
                        const previous = participantAnalytics.previousYearLast6Months.find(
                            p => p.month === current.month
                        );

                        // FIX: Explicitly convert the numeric month to a short string (e.g., 1 -> "Jan")
                        // We do not use '??' because current.month is likely a defined number/string (e.g. "1")
                        const formattedMonth = new Date(2000, Number(current.month) - 1, 1)
                            .toLocaleString('en-US', { month: 'short' });

                        return {
                            month: formattedMonth,
                            currentYearParticipantAdded: current.participantsAdded,
                            lastYearParticipantAdded: previous?.participantsAdded ?? 0,
                        };
                    }) ?? [];

                return participantTrends;
            case OwnerGraphTrend.Task:
                return taskAnalytics ?? []
            case OwnerGraphTrend.Billing:
                return billingAnalytics?.data ?? []
        }

        return [];
    }

    const graphData = createGraphData();

    const lastQuarter = currentGraphTrend === OwnerGraphTrend.Participants
        ? participantAnalytics?.change
        : currentGraphTrend === OwnerGraphTrend.Billing
            ? billingAnalytics?.change
            : "0"

    return (
        <div className="ow_nw_box">
            <div className="ow_nw_header_row">
                <div>
                    <h3 className="text-[16px] xl:text-[18px] 2xl:text-[20px] font-semibold">{currentGraphTrend} Trends</h3>
                    <p className="ow_nw_subtitle">6-month incident analysis</p>
                </div>

                {/* Optional: You can apply the same color logic here as OverviewHeader if desired */}
                <div className="ow_nw_badge"> {lastQuarter}% vs last quarter</div>
            </div>

            <div className="ow_nw_chart">
                <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={graphData || []}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />

                        {renderLines()}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default ParticipantTrends;